"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PublicKey } from "@solana/web3.js";
import useWallet from "@/hooks/useWallet";
import { createLaunchlabBrowserConnection } from "@/lib/idx-launchlab/rpc";
import { WALLET_CONNECTION_UPDATED_EVENT, WALLET_DISCONNECTED_EVENT } from "@/utils/walletConnectAttention";

/**
 * Launchlab wallet bridge — uses the app header wallet (adapter + direct injectors),
 * not wallet-adapter-react alone (MetaMask / IDX Wallet connect there without adapter publicKey).
 *
 * RPC for LaunchLab is Next/frontend only — never Autobot `/api/solana-rpc`.
 */
export function useLaunchlabWallet() {
  const {
    isConnected: walletIsConnected,
    walletAddress,
    solanaPublicKey,
    signTransaction,
    signAllTransactions,
    sendTransaction,
    canSignTransaction,
    getSolanaBalance,
    connectedWallet,
  } = useWallet();

  const connection = useMemo(() => createLaunchlabBrowserConnection(), []);

  const [connectionRevision, setConnectionRevision] = useState(0);
  const [balanceSol, setBalanceSol] = useState(null);
  const [balanceLamports, setBalanceLamports] = useState(null);
  const lastBalanceFetchAtRef = useRef(0);
  const lastWalletKeyRef = useRef(null);

  const WALLET_BALANCE_POLL_MS = 60_000;
  const WALLET_BALANCE_MIN_GAP_MS = 15_000;

  useEffect(() => {
    if (typeof window === "undefined") return;
    const bump = () => setConnectionRevision((value) => value + 1);
    window.addEventListener(WALLET_CONNECTION_UPDATED_EVENT, bump);
    window.addEventListener(WALLET_DISCONNECTED_EVENT, bump);
    return () => {
      window.removeEventListener(WALLET_CONNECTION_UPDATED_EVENT, bump);
      window.removeEventListener(WALLET_DISCONNECTED_EVENT, bump);
    };
  }, []);

  // Re-sync when shared wallet hook state changes (connect/disconnect/switch wallet).
  useEffect(() => {
    setConnectionRevision((value) => value + 1);
  }, [walletIsConnected, walletAddress, solanaPublicKey]);

  // Only trust live hook/adapter state — never fall back to localStorage after disconnect.
  const resolvedAddress = useMemo(() => {
    void connectionRevision;
    if (!walletIsConnected) return "";
    const fromHook = walletAddress?.trim() ?? "";
    if (fromHook) return fromHook;
    if (solanaPublicKey) return solanaPublicKey.toBase58();
    return "";
  }, [connectionRevision, solanaPublicKey, walletAddress, walletIsConnected]);

  const publicKey = useMemo(() => {
    if (!walletIsConnected) return null;
    if (solanaPublicKey) return solanaPublicKey;
    if (!resolvedAddress) return null;
    try {
      return new PublicKey(resolvedAddress);
    } catch {
      return null;
    }
  }, [resolvedAddress, solanaPublicKey, walletIsConnected]);

  const isConnected = Boolean(walletIsConnected && publicKey);

  const canSign = Boolean(isConnected && publicKey && canSignTransaction);

  const refreshWalletBalance = useCallback(async (opts = {}) => {
    const force = opts.force === true;
    if (!publicKey || !isConnected) {
      setBalanceSol(null);
      setBalanceLamports(null);
      return;
    }

    const now = Date.now();
    if (!force && now - lastBalanceFetchAtRef.current < WALLET_BALANCE_MIN_GAP_MS) {
      return;
    }
    lastBalanceFetchAtRef.current = now;

    try {
      const lamports = await connection.getBalance(publicKey, "confirmed");
      setBalanceLamports(lamports);
      setBalanceSol(lamports / 1e9);
      return;
    } catch {
      /* fall back to shared wallet helper (includes fallback RPC) */
    }

    const str = await getSolanaBalance(publicKey);
    const parsed = Number.parseFloat(str);
    if (Number.isFinite(parsed)) {
      setBalanceSol(parsed);
      setBalanceLamports(Math.round(parsed * 1e9));
      return;
    }

    setBalanceSol(null);
    setBalanceLamports(null);
  }, [connection, getSolanaBalance, isConnected, publicKey]);

  useEffect(() => {
    const walletKey = publicKey?.toBase58() ?? null;
    if (walletKey !== lastWalletKeyRef.current) {
      lastBalanceFetchAtRef.current = 0;
      lastWalletKeyRef.current = walletKey;
    }
    if (!isConnected) {
      lastBalanceFetchAtRef.current = 0;
      lastWalletKeyRef.current = null;
    }
  }, [isConnected, publicKey]);

  useEffect(() => {
    void refreshWalletBalance({ force: true });
    const id = window.setInterval(() => void refreshWalletBalance(), WALLET_BALANCE_POLL_MS);
    return () => window.clearInterval(id);
  }, [refreshWalletBalance]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onFocus = () => void refreshWalletBalance();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") onFocus();
    });
    return () => {
      window.removeEventListener("focus", onFocus);
    };
  }, [refreshWalletBalance]);

  return {
    connection,
    isConnected,
    publicKey,
    signTransaction,
    signAllTransactions,
    sendTransaction,
    canSign,
    balanceSol,
    balanceLamports,
    refreshWalletBalance,
    /** Display name from adapter / direct provider (e.g. "Phantom"), or null. */
    connectedWallet: isConnected ? connectedWallet ?? null : null,
  };
}
